// Preview-body subcomponent for ManufacturingModelsBulkImportModal.

import type { RecipeBulkImportPreview } from "@/lib/manufacturing/recipeBulkImportPreview";
import { lineIsPendingMaterial, lineIsUnresolvedRef } from "@/lib/manufacturing/recipeBulkImportPreviewHelpers";

export function ManufacturingRecipeImportPreview({
  preview,
}: {
  preview: RecipeBulkImportPreview;
}) {
  if (preview.kind === "empty") return null;
  if (preview.kind === "parse-error") {
    return <div className="text-xs text-red-600 dark:text-red-400">{preview.message}</div>;
  }
  if (preview.kind === "first-row-error") {
    return (
      <div className="text-xs text-red-600 dark:text-red-400">
        First row is missing required keys: {preview.missing.join(", ")}. Add those
        columns/keys to your {preview.format.toUpperCase()} source and re-paste.
      </div>
    );
  }

  const { recipes, totals, skipped, ignoredKeys, mode } = preview;
  const skipRecipes = recipes.filter((r) => r.conflict === "code_exists");
  const replaceRecipes = recipes.filter((r) => r.conflict === "will_replace");
  const pendingRecipes = recipes.filter(
    (r) => r.conflict !== "code_exists" && !r.hasUnresolvedRefs && r.hasPending,
  );
  const unresolvedRecipes = recipes.filter(
    (r) => r.conflict !== "code_exists" && r.hasUnresolvedRefs,
  );

  return (
    <div className="space-y-1.5 text-xs text-gray-700 dark:text-zinc-300">
      <div>
        Detected <strong>{preview.format.toUpperCase()}</strong> · mode{" "}
        <strong>{mode}</strong> · <strong>{totals.recipes}</strong> recipe
        {totals.recipes === 1 ? "" : "s"} · <strong>{totals.linesTotal}</strong> line
        {totals.linesTotal === 1 ? "" : "s"}.
      </div>
      <div>
        <strong>{totals.recipesReadyToCreate}</strong> will create
        {totals.recipesWillReplace > 0 ? (
          <>
            {" "}
            · <strong>{totals.recipesWillReplace}</strong> will replace existing BOM lines
          </>
        ) : null}
        {totals.recipesWithPending > 0 ? (
          <>
            {" "}
            · <strong>{totals.recipesWithPending}</strong> have unmatched materials (pending)
          </>
        ) : null}
        {totals.recipesWithUnresolvedRefs > 0 ? (
          <>
            {" "}
            · <strong>{totals.recipesWithUnresolvedRefs}</strong> blocked (unknown sub-recipe /
            part-use code)
          </>
        ) : null}
        {totals.recipesConflicting > 0 ? (
          <>
            {" "}
            · <strong>{totals.recipesConflicting}</strong> skipped (code already exists)
          </>
        ) : null}
        .
      </div>
      <div>
        <strong>{totals.linesMatched}</strong> matched ·{" "}
        <strong>{totals.linesPending}</strong> pending materials ·{" "}
        <strong>{totals.linesUnresolved}</strong> unresolved refs.
      </div>
      {mode === "upsert" ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-950 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
          Every imported recipe is flagged <strong>needs review</strong> until you confirm.
          Unmatched material names stay as pending lines and block confirmation. Sub-recipes and
          part uses must already exist (matched by code) or the recipe is skipped. Replace updates
          wipe existing BOM lines and rewrite from the CSV.
        </div>
      ) : null}

      {ignoredKeys.length > 0 ? (
        <div className="text-gray-500 dark:text-zinc-400">
          Unrecognized keys (ignored): {ignoredKeys.join(", ")}
        </div>
      ) : null}

      {replaceRecipes.length > 0 ? (
        <details className="mt-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
          <summary className="cursor-pointer font-medium">
            Show {replaceRecipes.length} recipe
            {replaceRecipes.length === 1 ? "" : "s"} that will replace
          </summary>
          <ul className="mt-1 max-h-32 overflow-y-auto">
            {replaceRecipes.map((r) => (
              <li key={r.code}>
                <code>{r.code}</code> — {r.name}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {skipRecipes.length > 0 ? (
        <details className="mt-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800/40">
          <summary className="cursor-pointer font-medium">
            Show {skipRecipes.length} skipped (code exists)
          </summary>
          <ul className="mt-1 max-h-32 overflow-y-auto">
            {skipRecipes.map((r) => (
              <li key={r.code}>
                <code>{r.code}</code> — already exists in this org
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {unresolvedRecipes.length > 0 ? (
        <details className="mt-1 rounded border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] text-rose-900 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-100">
          <summary className="cursor-pointer font-medium">
            Show {unresolvedRecipes.length} blocked (unknown sub-recipe / part use)
          </summary>
          <ul className="mt-1 max-h-40 overflow-y-auto">
            {unresolvedRecipes.map((r) => {
              const bad = r.lines.filter(lineIsUnresolvedRef);
              return (
                <li key={r.code} className="py-0.5">
                  <code>{r.code}</code> · {r.name} —{" "}
                  {bad
                    .slice(0, 4)
                    .map((p) =>
                      p.line_kind === "component"
                        ? p.component_recipe_code
                        : p.material_use_code,
                    )
                    .join(", ")}
                  {bad.length > 4 ? "…" : ""}
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}

      {pendingRecipes.length > 0 ? (
        <details className="mt-1 rounded border border-orange-200 bg-orange-50 px-2 py-1 text-[11px] text-orange-900 dark:border-orange-800 dark:bg-orange-900/20 dark:text-orange-100">
          <summary className="cursor-pointer font-medium">
            Show {pendingRecipes.length} recipe
            {pendingRecipes.length === 1 ? "" : "s"} with unmatched materials
          </summary>
          <ul className="mt-1 max-h-40 overflow-y-auto">
            {pendingRecipes.map((r) => {
              const pending = r.lines.filter(lineIsPendingMaterial);
              return (
                <li key={r.code} className="py-0.5">
                  <code>{r.code}</code> · {r.name} —{" "}
                  <strong>{pending.length}</strong> unmatched:
                  <span className="text-orange-800 dark:text-orange-200">
                    {" "}
                    {pending
                      .slice(0, 4)
                      .map((p) => p.material_name)
                      .join(", ")}
                    {pending.length > 4 ? "…" : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        </details>
      ) : null}

      {skipped.length > 0 ? (
        <ul className="mt-1 max-h-32 overflow-y-auto rounded border border-gray-100 bg-gray-50 px-2 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800/40">
          {skipped.map((s, i) => (
            <li key={`${s.context}-${s.idx}-${i}`}>
              {s.context === "recipe-header" ? "Recipe row" : "Line row"} {s.idx + 1}: missing{" "}
              {s.missing.join(", ")}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
